import type { ConsentPreferences } from '@/privacy/consent';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';


interface ConsentModalProps {
  isOpen: boolean;
  preferences: ConsentPreferences;
  onClose: () => void;
  onSave: (preferences: ConsentPreferences) => void;
}

const focusableSelectors = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const ConsentModal: React.FC<ConsentModalProps> = ({ isOpen, preferences, onClose, onSave }) => {
  const { t } = useTranslation('privacy');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [localPreferences, setLocalPreferences] = useState<ConsentPreferences>(preferences);
  const previouslyFocusedRef = useRef<Element | null>(null);

  useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (!isOpen) {
      if (previouslyFocusedRef.current instanceof HTMLElement) {
        previouslyFocusedRef.current.focus();
      }
      document.body.style.removeProperty('overflow');
      return undefined;
    }

    document.body.style.setProperty('overflow', 'hidden');
    previouslyFocusedRef.current = document.activeElement;

    const focusDialog = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }

      const focusable = dialog.querySelectorAll<HTMLElement>(focusableSelectors);

      if (focusable.length > 0) {
        window.setTimeout(() => focusable[0].focus(), 0);
      } else {
        window.setTimeout(() => dialog.focus(), 0);
      }
    };

    focusDialog();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const dialog = dialogRef.current;

      if (!dialog) {
        return;
      }

      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelectors));

      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      } else if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen, onClose]);

  const handleToggle =
    (category: keyof ConsentPreferences) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalPreferences((prev) => ({
        ...prev,
        [category]: event.target.checked,
      }));
    };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave(localPreferences);
  };

  const dialogClassName = useMemo(
    () => `privacy-modal${isOpen ? ' privacy-modal--open' : ''}`,
    [isOpen],
  );

  return (
    <div className={dialogClassName} hidden={!isOpen} role="presentation">
      <div className="privacy-modal__backdrop" aria-hidden="true" onClick={onClose} />
      <div
        ref={dialogRef}
        className="privacy-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-modal-title"
        tabIndex={-1}
      >
        <form className="privacy-modal__form" onSubmit={handleSubmit}>
          <header className="privacy-modal__header">
            <h2 id="privacy-modal-title" className="privacy-modal__title">
              {t('modal.title')}
            </h2>
            <p className="privacy-modal__description">{t('modal.description')}</p>
          </header>
          <div className="privacy-modal__body">
            <fieldset className="privacy-modal__group" aria-describedby="privacy-modal-essential">
              <legend className="privacy-modal__legend">
                {t('modal.categories.essential.title')}
              </legend>
              <p id="privacy-modal-essential" className="privacy-modal__hint">
                {t('modal.categories.essential.description')}
              </p>
              <div className="form-check form-switch privacy-modal__switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="privacy-modal-essential-toggle"
                  checked
                  readOnly
                />
                <label className="form-check-label" htmlFor="privacy-modal-essential-toggle">
                  {t('modal.categories.essential.label')}
                </label>
              </div>
            </fieldset>
            <fieldset className="privacy-modal__group" aria-describedby="privacy-modal-analytics">
              <legend className="privacy-modal__legend">
                {t('modal.categories.analytics.title')}
              </legend>
              <p id="privacy-modal-analytics" className="privacy-modal__hint">
                {t('modal.categories.analytics.description')}
              </p>
              <div className="form-check form-switch privacy-modal__switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="privacy-modal-analytics-toggle"
                  checked={localPreferences.analytics}
                  onChange={handleToggle('analytics')}
                />
                <label className="form-check-label" htmlFor="privacy-modal-analytics-toggle">
                  {t('modal.categories.analytics.label')}
                </label>
              </div>
            </fieldset>
            <fieldset className="privacy-modal__group" aria-describedby="privacy-modal-marketing">
              <legend className="privacy-modal__legend">
                {t('modal.categories.marketing.title')}
              </legend>
              <p id="privacy-modal-marketing" className="privacy-modal__hint">
                {t('modal.categories.marketing.description')}
              </p>
              <div className="form-check form-switch privacy-modal__switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="privacy-modal-marketing-toggle"
                  checked={localPreferences.marketing}
                  onChange={handleToggle('marketing')}
                />
                <label className="form-check-label" htmlFor="privacy-modal-marketing-toggle">
                  {t('modal.categories.marketing.label')}
                </label>
              </div>
            </fieldset>
          </div>
          <footer className="privacy-modal__footer">
            <button type="button" className="btn btn-link privacy-modal__button" onClick={onClose}>
              {t('modal.cancel')}
            </button>
            <button type="submit" className="btn btn-primary privacy-modal__button">
              {t('modal.save')}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default ConsentModal;
